/**
 * @fileoverview Componente responsável por renderizar uma mensagem individual na lista do ticket.
 */

import React, { memo, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import clsx from "clsx";
import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import { AccessTime, Done } from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import { BarChart3, Bot, CornerDownLeft, ExternalLink, FileText as FileTextIcon, Image as ImageIconLucide, Phone as PhoneIconLucide, Video as VideoIconLucide, ChevronDown, CheckCheck, Download, Ban, MapPin, AlertCircle, Sparkles } from "lucide-react";
import MarkdownWrapper from "../MarkdownWrapper";
import RotatableImage from "../RotatableImage";
import VideoPlayer from "../VideoPlayer";
import LocationPreview from "../LocationPreview";
import SharedContactCard from "../SharedContactCard";
import { getSharedContacts, sharedContactsLabel } from "../../utils/sharedContacts";
import AudioPlayer from "../AudioPlayer";
import { i18n } from "../../translate/i18n";
import { tr as smartApproachText } from "../SmartApproach/text";
import { ordinal } from "../../utils/smartApproachText";
import { getDocumentInfo, DOC_EXT_COLORS } from "./documentMeta";

// Helpers de mídia
const isType = (t = "", name) => String(t || "").toLowerCase().includes(name);
const isImage = (t) => isType(t, "image");
const isAudio = (t) => isType(t, "audio") || isType(t, "ptt");
const isVideo = (t) => isType(t, "video");
const isDocument = (t) => isType(t, "application") || isType(t, "document") || isType(t, "pdf");

const isImageFile = (mediaUrl = "", body = "") => {
  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i;
  return imageExtensions.test(mediaUrl) || imageExtensions.test(body);
};

const isVideoFile = (mediaUrl = "", body = "") => {
  const videoExtensions = /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)$/i;
  return videoExtensions.test(mediaUrl) || videoExtensions.test(body);
};

const isAudioFile = (mediaUrl = "", body = "") => {
  const audioExtensions = /\.(mp3|wav|ogg|aac|m4a|opus)$/i;
  return audioExtensions.test(mediaUrl) || audioExtensions.test(body);
};

// Paleta de cores para nomes em grupos
const nameColorPalette = [
  "#e53935", "#8e24aa", "#3949ab", "#1e88e5", "#00acc1",
  "#00897b", "#43a047", "#f4511e", "#6d4c41", "#546e7a"
];

const hashString = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const getStableColor = (name = "") => {
  const hash = hashString(name.trim().toLowerCase());
  return nameColorPalette[hash % nameColorPalette.length];
};

// Helper para extrair o nome do remetente em mensagens de grupo
const getSenderName = (message, isGroup) => {
  if (!isGroup || message.fromMe) {
    return message.contact?.name;
  }

  // Para grupos, tentar extrair pushName do dataJson
  try {
    if (message.dataJson) {
      const data = JSON.parse(message.dataJson);
      // pushName está no nível raiz do dataJson
      if (data.pushName) {
        return data.pushName;
      }
    }
  } catch (e) {
    console.error("Erro ao extrair pushName:", e);
  }

  // Fallback para contact.name
  return message.contact?.name;
};

const getExcerpt = (text = "", max = 120) => {
  const normalized = String(text).replace(/\s+/g, " ").trim();
  if (normalized.length <= max) return normalized;
  return normalized.slice(0, max - 1) + "…";
};

// Helper para extrair menções do dataJson
const getMentionedJid = (message) => {
  try {
    if (message.dataJson) {
      const data = JSON.parse(message.dataJson);
      // Tentar extrair de message.extendedTextMessage.contextInfo.mentionedJid
      const mentioned = data.message?.extendedTextMessage?.contextInfo?.mentionedJid;
      if (mentioned && Array.isArray(mentioned)) {
        return mentioned;
      }
    }
  } catch (e) {
    console.error("Erro ao extrair mentionedJid:", e);
  }
  return null;
};

// Menções enviadas pelo próprio sistema — gravadas em dataJson._mentionsMeta
// no formato [{ label: "@Maria", number: "5511...", jid: "5511...@c.us" }].
const getMentionsMeta = (message) => {
  try {
    if (message.dataJson) {
      const data = JSON.parse(message.dataJson);
      const meta = data?._mentionsMeta;
      if (Array.isArray(meta) && meta.length > 0) return meta;
    }
  } catch {}
  return null;
};

const useStyles = makeStyles((theme) => ({
  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === 'light' ? "#ffffff" : "#242626",
    color: theme.palette.type === 'light' ? "#303030" : "#EAEAEA",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.type === 'light' ? "0 1px 1px #b3b3b3" : "none",
  },
  messageLeftMedia: {
    maxWidth: 260,
  },
  stickerBubble: {
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
    padding: "0 !important",
    border: "none !important",
    maxWidth: 180,
    minWidth: "unset !important",
    borderRadius: "0 !important",
  },
  stickerImage: {
    width: 160,
    height: "auto",
    maxHeight: 160,
    objectFit: "contain",
    display: "block",
  },
  stickerTimestamp: {
    fontSize: 11,
    color: theme.palette.type === 'light' ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.55)",
    marginTop: 2,
    paddingLeft: 4,
  },
  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === 'light' ? "#DCF8C6" : "#274D37",
    color: theme.palette.type === 'light' ? "#303030" : "#EAEAEA",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.type === 'light' ? "0 1px 1px #b3b3b3" : "none",
  },
  messageRightMedia: {
    maxWidth: 260,
  },
  // Balão enviado pela IA: verde-azulado mais fundo, contorno inteiro e a linha
  // com o nome da IA e o selo "IA" no topo (o atendente reconhece de relance).
  messageAi: {
    backgroundColor: theme.palette.type === "light" ? "#e2f5ef" : "#11332d",
    border: `1px solid ${theme.palette.type === "light" ? "#7fd3bb" : "rgba(45, 212, 160, 0.6)"}`,
  },
  aiHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "2px 6px 3px",
    whiteSpace: "nowrap",
    color: theme.palette.type === "light" ? "#00745e" : "#4fe0b5",
  },
  aiAvatar: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: theme.palette.type === "light" ? "rgba(0, 116, 94, 0.12)" : "rgba(79, 224, 181, 0.16)",
    "& svg": { width: 13, height: 13, strokeWidth: 2 },
  },
  aiName: {
    fontSize: 13,
    fontWeight: 600,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  aiBadge: {
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    lineHeight: "16px",
    padding: "0 6px",
    borderRadius: 999,
    backgroundColor: theme.palette.type === "light" ? "rgba(0, 116, 94, 0.1)" : "rgba(79, 224, 181, 0.14)",
  },
  // Envio automático da abordagem inteligente: linha neutra no topo do balão,
  // sem o verde da IA (a mensagem não é de ninguém).
  autoHeader: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "2px 6px 3px",
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.type === "light" ? "#475569" : "rgba(255, 255, 255, 0.64)",
    "& svg": { width: 13, height: 13, flexShrink: 0 },
    // Em balão estreito, quebra em linhas parecidas (sem deixar "3)" sozinho).
    "& span": { textWrap: "balance" },
  },
  messageSelected: {
    backgroundColor: `${theme.palette.primary.main}10`,
    borderRadius: theme.shape.borderRadius,
  },
  messageContactName: {
    display: "flex",
    color: "inherit",
    fontWeight: 500,
  },
  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },
  textContentItemEdited: {
    overflowWrap: "break-word",
    padding: "3px 120px 6px 6px",
  },
  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.36)" : "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
    opacity: 0.5,
  },
  // Cartão de contato: ocupa a largura do balão e a hora fica embaixo dele.
  textContentItemCard: {
    padding: "3px 3px 18px 3px",
  },
  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  messageMediaDeleted: {
    opacity: 0.5,
    filter: "grayscale(50%)",
  },
  deletedMediaOverlay: {
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.1)",
      borderRadius: 8,
    }
  },
  audioControl: {
    width: '100%',
    height: 36,
    backgroundColor: 'transparent',
    filter: theme.palette.type === 'light' ? 'none' : 'invert(0.85)',
    color: theme.palette.type === 'light' ? '#303030' : '#EAEAEA',
  },
  audioWrapper: {
    display: 'block',
    width: '100%',
    minWidth: 220,
    maxWidth: 420,
    padding: '4px 6px 2px 6px',
  },
  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },
  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },
  ackDoneAllIcon: {
    color: "#53bdeb",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  ackErrorIcon: {
    color: "#f44336",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
    cursor: "pointer",
  },
  errorTooltip: {
    backgroundColor: "#1e1e1e",
    color: "#fff",
    fontSize: "0.8rem",
    lineHeight: 1.5,
    padding: "10px 14px",
    maxWidth: 340,
    borderRadius: 8,
    whiteSpace: "pre-line",
    border: "1px solid rgba(244, 67, 54, 0.3)",
  },
  errorTooltipArrow: {
    color: "#1e1e1e",
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
    "& .MuiButton-label": {
      color: "#ffffff",
    },
  },
  documentContainer: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    margin: "4px 0",
    borderRadius: 8,
    backgroundColor: theme.palette.type === 'light' ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)",
    cursor: "pointer",
    gap: 10,
    minWidth: 240,
    "&:hover": {
      backgroundColor: theme.palette.type === 'light' ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.10)",
    },
  },
  documentIcon: {
    width: 34,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
    textTransform: "uppercase",
  },
  documentInfo: {
    flex: 1,
    minWidth: 0,
  },
  documentName: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.type === 'light' ? "#303030" : "#e9edef",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  documentMeta: {
    fontSize: 11,
    color: theme.palette.type === 'light' ? "#667781" : "#8696a0",
    marginTop: 2,
  },
  documentDownload: {
    width: 30,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    border: `1.5px solid ${theme.palette.type === 'light' ? "#00a884" : "#00a884"}`,
    flexShrink: 0,
    color: theme.palette.type === 'light' ? "#00a884" : "#00a884",
  },
  reactionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
    marginBottom: 4,
    paddingLeft: 5,
    paddingRight: 5,
  },
  reactionBubble: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: theme.palette.type === 'light' ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.1)",
    borderRadius: 12,
    padding: "2px 8px",
    fontSize: 14,
    border: `1px solid ${theme.palette.type === 'light' ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.2)"}`,
  },
  reactionEmoji: {
    fontSize: 16,
    marginRight: 4,
  },
  reactionCount: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.type === 'light' ? "#303030" : "#EAEAEA",
  },
  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.type === 'light' ? "#e8e8e8" : "#1c1e1e",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },
  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.type === 'light' ? "#c0dea3" : "#1f3f2c",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },
  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    color: theme.palette.type === 'light' ? "#303030" : "#d1d7db",
  },
  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.type === 'light' ? "#6bcbef" : "#53bdeb",
  },
  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.type === 'light' ? "#35cd96" : "#06cf9c",
  },

  // Card compacto pra documento dentro do preview de reply
  quotedDocCard: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 0",
    minWidth: 0,
  },
  quotedDocIcon: {
    width: 28,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  quotedDocInfo: {
    flex: 1,
    minWidth: 0,
  },
  quotedDocName: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.type === 'light' ? "#303030" : "#e9edef",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  quotedDocMeta: {
    fontSize: 10,
    color: theme.palette.type === 'light' ? "#667781" : "#8696a0",
    marginTop: 1,
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },
  // Meta template rendering (HEADER / FOOTER / BUTTONS na bolha)
  tplHeaderText: {
    fontWeight: 700,
    fontSize: 14,
    marginBottom: 4,
    color: theme.palette.type === "dark" ? "#f3f4f6" : "#111b21",
  },
  tplHeaderMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    height: 96,
    marginBottom: 6,
    borderRadius: 8,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
    border: theme.palette.type === "dark" ? "1px dashed rgba(255,255,255,0.12)" : "1px dashed rgba(15,23,42,0.1)",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#64748b",
    fontSize: 12,
    fontWeight: 500,
  },
  tplFooter: {
    fontSize: 12,
    marginTop: 6,
    opacity: 0.6,
    fontStyle: "italic",
    color: theme.palette.type === "dark" ? "rgba(229,231,235,0.6)" : "rgba(17,27,33,0.6)",
  },
  tplButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 8,
    marginBottom: 4,
  },
  tplButton: {
    // 2 por linha (50/50). 1 botao sozinho cresce pra ocupar a linha inteira.
    flex: "1 1 calc(50% - 4px)",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "7px 10px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
    color: theme.palette.type === "dark" ? "#60a5fa" : "#027eb5",
    border: theme.palette.type === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.06)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  // ─── Enquete (poll) ───
  pollWrap: {
    marginTop: 2,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 260,
  },
  pollHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.type === "dark" ? "rgba(229,231,235,0.55)" : "rgba(17,27,33,0.55)",
  },
  pollQuestion: {
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.type === "dark" ? "#f3f4f6" : "#111b21",
    wordBreak: "break-word",
    marginBottom: 4,
  },
  pollOptions: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  pollOption: {
    position: "relative",
    padding: "8px 10px",
    borderRadius: 8,
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.04)"
      : "rgba(15,23,42,0.035)",
    overflow: "hidden",
    isolation: "isolate",
  },
  pollOptionLeader: {
    background: theme.palette.type === "dark"
      ? "rgba(16,185,129,0.08)"
      : "rgba(16,185,129,0.06)",
  },
  pollFill: {
    position: "absolute",
    inset: 0,
    background: theme.palette.type === "dark"
      ? "rgba(99,102,241,0.18)"
      : "rgba(99,102,241,0.12)",
    transformOrigin: "left center",
    transition: "transform 360ms cubic-bezier(0.22, 1, 0.36, 1)",
    zIndex: -1,
    borderRadius: 8,
  },
  pollFillLeader: {
    background: theme.palette.type === "dark"
      ? "rgba(16,185,129,0.22)"
      : "rgba(16,185,129,0.16)",
  },
  pollOptionRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  pollOptionLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#e5e7eb" : "#111b21",
    wordBreak: "break-word",
  },
  pollOptionTally: {
    fontSize: 12,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.type === "dark" ? "rgba(229,231,235,0.7)" : "rgba(17,27,33,0.65)",
    fontWeight: 600,
    minWidth: 32,
    textAlign: "right",
  },
  pollFooter: {
    fontSize: 11,
    color: theme.palette.type === "dark" ? "rgba(229,231,235,0.45)" : "rgba(17,27,33,0.45)",
    marginTop: 2,
  },
}));

// Extrai estrutura de template Meta (header/footer/buttons) gravada em dataJson
// pelo MetaTemplateController. Retorna null quando nao ha template.
const parseMetaTemplate = (message) => {
  if (!message?.dataJson) return null;
  try {
    const parsed = JSON.parse(message.dataJson);
    return parsed?.metaTemplate || null;
  } catch {
    return null;
  }
};

// Marca do envio automático da abordagem inteligente ({ step, total }), gravada
// em dataJson.smartApproach pelo motor. null: mensagem comum.
const parseSmartApproachMark = (dataJson) => {
  if (!dataJson) return null;
  try {
    const mark = JSON.parse(dataJson)?.smartApproach;
    return Number.isInteger(mark?.step) && Number.isInteger(mark?.total) ? mark : null;
  } catch {
    return null;
  }
};

// Detecta se a mensagem e uma enquete (pollCreationMessage V1/V2/V3) e extrai
// pergunta + opcoes + totalizador de votos por opcao (a partir de _pollVotes
// gravado pelo handler do webhook).
const parsePollMessage = (message) => {
  if (!message) return null;
  const mt = message.mediaType || "";
  const isPollType = mt.toLowerCase().startsWith("pollcreation");
  if (!isPollType && !message.dataJson) return null;

  let data = {};
  try {
    data = message.dataJson ? JSON.parse(message.dataJson) : {};
  } catch {
    data = {};
  }

  // WAMessage fica em data.message ou data.Message (case-insensitive)
  const msg = data.message || data.Message || {};
  const poll =
    msg.pollCreationMessage ||
    msg.pollCreationMessageV2 ||
    msg.pollCreationMessageV3 ||
    msg.pollcreationmessage ||
    null;
  if (!poll) return null;

  const question = poll.name || poll.question || message.body || "Enquete";
  const options = (poll.options || []).map((o, idx) => ({
    name: o.optionName || o.name || o.title || `Opção ${idx + 1}`,
    hash: o.optionHash || o.hash || null,
    index: idx,
  }));
  if (options.length === 0) return null;

  // Totaliza votos: cada entrada de _pollVotes e o conjunto COMPLETO de
  // opcoes votadas por aquele participante — substitui, nao acumula.
  const votes = data._pollVotes || {};
  const tallyByKey = {};
  let voterCount = 0;
  Object.values(votes).forEach((entry) => {
    const picks = (entry && entry.options) || [];
    if (picks.length > 0) voterCount += 1;
    picks.forEach((raw) => {
      const key = String(raw);
      tallyByKey[key] = (tallyByKey[key] || 0) + 1;
      // Duplica em lowercase para matching case-insensitive por nome
      const lower = key.toLowerCase();
      if (lower !== key) tallyByKey[lower] = (tallyByKey[lower] || 0) + 1;
    });
  });

  const withTally = options.map((o) => {
    // Match ordenado: hash > nome literal > nome lowercased > indice
    const tryKeys = [];
    if (o.hash) tryKeys.push(String(o.hash));
    if (o.name) {
      tryKeys.push(String(o.name));
      tryKeys.push(String(o.name).toLowerCase());
    }
    tryKeys.push(String(o.index));
    let count = 0;
    for (const k of tryKeys) {
      if (tallyByKey[k] !== undefined) {
        count = tallyByKey[k];
        break;
      }
    }
    return { ...o, count };
  });

  const total = withTally.reduce((s, o) => s + o.count, 0);
  const maxCount = withTally.reduce((m, o) => Math.max(m, o.count), 0);

  return {
    question,
    options: withTally,
    total,
    maxCount,
    voterCount,
    selectableCount: poll.selectableOptionsCount || 1,
  };
};

// ✅ Helper para agrupar reações
const groupReactions = (reactions = []) => {
  if (!reactions || !Array.isArray(reactions)) return [];
  const grouped = {};
  reactions.forEach(reaction => {
    const emoji = reaction.text;
    if (!emoji) return;
    if (!grouped[emoji]) {
      grouped[emoji] = { emoji, count: 0, users: [] };
    }
    grouped[emoji].count++;
    grouped[emoji].users.push({
      fromMe: reaction.fromMe,
      participant: reaction.participant
    });
  });
  return Object.values(grouped);
};

const MessageItem = memo(({
  message,
  isGroup,
  forwardMode,
  selectedMessages,
  onOpenMessageOptionsMenu,
  onMessageSelect,
  onQuotedClick
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const downloadedKey = `doc_dl_${message.id}`;
  const [docDownloadState, setDocDownloadState] = useState(
    () => localStorage.getItem(downloadedKey) ? "done" : null
  ); // null | "downloading" | "done"

  // Parse de dataJson: memoizado por dataJson (muda em update de voto/template)
  // + mediaType (muda em criacao). Evita JSON.parse a cada render.
  const parsedPoll = useMemo(
    () => parsePollMessage(message),
    [message.dataJson, message.mediaType]
  );
  const parsedTemplate = useMemo(
    () => parseMetaTemplate(message),
    [message.dataJson]
  );
  const smartApproachMark = useMemo(
    () => parseSmartApproachMark(message.dataJson),
    [message.dataJson]
  );
  // Contato compartilhado (vCard): cartão salvo no dataJson ou, sem ele, o texto.
  const sharedContacts = useMemo(
    () =>
      message.mediaType === "contactMessage" || message.mediaType === "vcard"
        ? getSharedContacts(message)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [message.dataJson, message.body, message.mediaType]
  );
  const hasContactCard = sharedContacts?.length > 0;

  const checkMessageMedia = (message) => {
    const isDeleted = message.isDeleted;
    const mediaStyle = isDeleted ? { opacity: 0.5, filter: "grayscale(50%)" } : {};

    if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|');
      let imageLocation = locationParts[0];
      let linkLocation = locationParts[1];
      let descriptionLocation = locationParts.length > 2 ? message.body.split('|')[2] : null;
      return (
        <div style={mediaStyle}>
          <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
        </div>
      );
    } else if (message.mediaType === "sticker") {
      return (
        <div style={mediaStyle}>
          <img
            src={message.mediaUrl}
            alt="sticker"
            className={classes.stickerImage}
          />
        </div>
      );
    } else if (message.mediaType === "image" || isImageFile(message.mediaUrl, message.body)) {
      return (
        <div style={mediaStyle}>
          <RotatableImage imageUrl={message.mediaUrl} />
        </div>
      );
    } else if (message.mediaType === "audio" || isAudioFile(message.mediaUrl, message.body)) {
      return <AudioPlayer audioUrl={message.mediaUrl} isDeleted={isDeleted} fromMe={message.fromMe} />;
    } else if (message.mediaType === "video" || isVideoFile(message.mediaUrl, message.body)) {
      return (
        <div style={mediaStyle}>
          <VideoPlayer videoUrl={message.mediaUrl} className={classes.messageMedia} />
        </div>
      );
    } else if (message.mediaType === "vcard" || message.mediaType === "contactMessage") {
      return null;
    } else {
      const { fileName, fileSize, ext } = getDocumentInfo(message);
      const extColor = DOC_EXT_COLORS[ext] || "#667781";

      const handleDownload = async (e) => {
        e.preventDefault();
        if (docDownloadState === "downloading") return;
        setDocDownloadState("downloading");
        try {
          const response = await fetch(message.mediaUrl);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName || "documento";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
          localStorage.setItem(downloadedKey, "1");
          setDocDownloadState("done");
        } catch (err) {
          console.error("Download failed:", err);
          setDocDownloadState(null);
          window.open(message.mediaUrl, "_blank");
        }
      };

      return (
        <div
          className={classes.documentContainer}
          onClick={handleDownload}
          style={{ cursor: "pointer" }}
        >
          <div className={classes.documentIcon} style={{ backgroundColor: extColor }}>
            {ext || "DOC"}
          </div>
          <div className={classes.documentInfo}>
            <div className={classes.documentName} title={fileName}>
              {fileName}
            </div>
            <div className={classes.documentMeta}>
              {[ext, fileSize].filter(Boolean).join(" • ")}
            </div>
          </div>
          {docDownloadState !== "done" && (
            <div className={classes.documentDownload}>
              {docDownloadState === "downloading" ? (
                <CircularProgress size={18} style={{ color: "#00a884" }} />
              ) : (
                <Download size={18} />
              )}
            </div>
          )}
        </div>
      );
    }
  };

  // Decide o conteudo da bolha: enquete > template Meta > body normal.
  // Usa parses memoizados (parsedPoll / parsedTemplate) — sem JSON.parse por render.
  const renderMessageContent = () => {
    if (parsedPoll) return renderPoll(parsedPoll);
    if (hasContactCard) {
      return <SharedContactCard contacts={sharedContacts} ticketId={message.ticketId} disabled={message.isDeleted} />;
    }

    // body só não é texto livre quando é dado estruturado (location) ou URL
    // de mídia legacy. Contato sem cartão legível mostra o texto (nunca vazio).
    // `checkMessageMedia` renderiza apenas a mídia — o caption fica aqui.
    const bodyIsNotCaption =
      message.mediaType === "locationMessage" ||
      isImageFile("", message.body) ||
      isVideoFile("", message.body) ||
      isAudioFile("", message.body);

    const bodyNode = (
      <MarkdownWrapper
        mentionedJid={getMentionedJid(message)}
        mentionsMeta={getMentionsMeta(message)}
      >
        {bodyIsNotCaption ? null : message.body}
      </MarkdownWrapper>
    );
    if (!parsedTemplate) return bodyNode;
    return (
      <>
        {parsedTemplate.header && renderTemplateHeader(parsedTemplate.header)}
        {bodyNode}
        {parsedTemplate.footer?.text &&
          renderTemplateFooter(parsedTemplate.footer.text)}
        {parsedTemplate.buttons?.length > 0 &&
          renderTemplateButtons(parsedTemplate.buttons)}
      </>
    );
  };

  // Renderiza bolha de enquete (pollCreationMessage)
  const renderPoll = (poll) => {
    if (!poll) return null;
    return (
      <div className={classes.pollWrap}>
        <div className={classes.pollHeaderRow}>
          <BarChart3 size={13} strokeWidth={2} /> Enquete
          {(poll.selectableCount === 0 || poll.selectableCount > 1) && (
            <span style={{ opacity: 0.65, fontWeight: 500, textTransform: "none" }}>
              · múltipla escolha
            </span>
          )}
        </div>
        <div className={classes.pollQuestion}>{poll.question}</div>
        <div className={classes.pollOptions}>
          {poll.options.map((opt, i) => {
            const pct = poll.total > 0 ? opt.count / poll.total : 0;
            const isLeader =
              poll.maxCount > 0 && opt.count === poll.maxCount;
            return (
              <div
                key={i}
                className={`${classes.pollOption} ${
                  isLeader ? classes.pollOptionLeader : ""
                }`}
              >
                <div
                  className={`${classes.pollFill} ${
                    isLeader ? classes.pollFillLeader : ""
                  }`}
                  style={{ transform: `scaleX(${pct})` }}
                />
                <div className={classes.pollOptionRow}>
                  <span className={classes.pollOptionLabel}>{opt.name}</span>
                  <span className={classes.pollOptionTally}>{opt.count}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className={classes.pollFooter}>
          {poll.voterCount === 0
            ? "Aguardando votos"
            : poll.voterCount === 1
            ? "1 voto"
            : `${poll.voterCount} votos`}
        </div>
      </div>
    );
  };

  // Renderiza a estrutura de template Meta (header / footer / buttons)
  // A renderizacao do BODY continua pelo MarkdownWrapper usual (message.body).
  const renderTemplateHeader = (header) => {
    if (!header) return null;
    if (header.format === "TEXT") {
      if (!header.text) return null;
      return <div className={classes.tplHeaderText}>{header.text}</div>;
    }
    // Preview real da mídia quando o backend informou a URL
    // (arquivo já no disco em /public/templateMedia/...).
    if (header.url) {
      if (header.format === "IMAGE") {
        return (
          <img
            src={header.url}
            alt={header.fileName || "imagem"}
            style={{
              display: "block",
              width: "100%",
              maxHeight: 260,
              objectFit: "cover",
              borderRadius: 8,
              marginBottom: 6,
            }}
          />
        );
      }
      if (header.format === "VIDEO") {
        return (
          <video
            src={header.url}
            controls
            style={{
              display: "block",
              width: "100%",
              maxHeight: 260,
              borderRadius: 8,
              marginBottom: 6,
              background: "#000",
            }}
          />
        );
      }
      if (header.format === "DOCUMENT") {
        return (
          <a
            href={header.url}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.tplHeaderMedia}
            style={{ textDecoration: "none" }}
          >
            <FileTextIcon size={16} strokeWidth={1.5} />
            {header.fileName || "Documento"}
          </a>
        );
      }
    }
    // Fallback (sem URL — templates antigos ou erro ao resolver)
    const iconMap = {
      IMAGE: { Icon: ImageIconLucide, label: "Imagem" },
      VIDEO: { Icon: VideoIconLucide, label: "Vídeo" },
      DOCUMENT: { Icon: FileTextIcon, label: "Documento" },
    };
    const match = iconMap[header.format];
    if (!match) return null;
    const { Icon, label } = match;
    return (
      <div className={classes.tplHeaderMedia}>
        <Icon size={16} strokeWidth={1.5} />
        {label}
      </div>
    );
  };

  const renderTemplateFooter = (text) => {
    if (!text) return null;
    return <div className={classes.tplFooter}>{text}</div>;
  };

  const renderTemplateButtons = (buttons) => {
    if (!buttons || buttons.length === 0) return null;
    return (
      <div className={classes.tplButtons}>
        {buttons.map((b, i) => {
          let Icon = CornerDownLeft;
          if (b.type === "URL") Icon = ExternalLink;
          else if (b.type === "PHONE_NUMBER") Icon = PhoneIconLucide;
          return (
            <div key={i} className={classes.tplButton}>
              <Icon size={14} strokeWidth={1.5} />
              {b.text}
            </div>
          );
        })}
      </div>
    );
  };

  const renderForwardedIndicator = (message, theme) => {
    let isForwarded = message.isForwarded;
    let forwardingScore = 0;
    let dataContext = null;

    try {
      if (message.dataJson) {
        const parsedData = JSON.parse(message.dataJson);
        dataContext = parsedData.message?.extendedTextMessage?.contextInfo ||
                     parsedData.message?.imageMessage?.contextInfo ||
                     parsedData.message?.videoMessage?.contextInfo ||
                     parsedData.message?.audioMessage?.contextInfo ||
                     parsedData.message?.documentMessage?.contextInfo ||
                     parsedData.message?.stickerMessage?.contextInfo ||
                     parsedData.message?.contactMessage?.contextInfo ||
                     parsedData.message?.locationMessage?.contextInfo ||
                     parsedData.message?.buttonsResponseMessage?.contextInfo ||
                     parsedData.message?.listResponseMessage?.contextInfo ||
                     parsedData.message?.templateButtonReplyMessage?.contextInfo ||
                     null;

        if (dataContext) {
          if (dataContext.forwardingScore && dataContext.forwardingScore > 0) {
            isForwarded = true;
            forwardingScore = dataContext.forwardingScore;
          }
          if (dataContext.isForwarded === true) {
            isForwarded = true;
          }
        }

        if (parsedData.message?.forwardingScore && parsedData.message.forwardingScore > 0) {
          isForwarded = true;
          forwardingScore = parsedData.message.forwardingScore;
        }
      }
    } catch (error) {
      console.error("Erro ao analisar dataJson da mensagem:", error);
    }

    if (isForwarded) {
      return (
        <div style={{
          display: "flex",
          alignItems: "center",
          fontSize: "11px",
          color: theme.palette.mode === 'light' ? "#4a4a4a" : "#8696a0",
          marginBottom: "4px",
          marginTop: "2px",
          fontWeight: "400"
        }}>
          <span style={{
            fontSize: "14px",
            marginRight: "4px",
            color: theme.palette.mode === 'light' ? "#4a4a4a" : "#8696a0",
            display: "inline-flex",
            alignItems: "center"
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M1.01 7L5 3c1.1 0 2 .9 2 2v2h8c1.1 0 2 .9 2 2v5l-2-2V9H7v2l-4-3.99L1.01 7zM15 15v2l4-3.99L15 9v2H7V7H5v2l-4-3.99L1.01 7L5 3c1.1 0 2 .9 2 2v2h8c1.1 0 2 .9 2 2v5l-2-2V9H7v2l-4-3.99L1.01 7z"/>
            </svg>
          </span>
          Encaminhada
          {forwardingScore > 1 && (
            <span style={{ marginLeft: "4px", fontSize: "10px", opacity: 0.8 }}>
              ({forwardingScore}x)
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  const renderMessageAck = (message) => {
    if (message.ack === -1) {
      return (
        <Tooltip
          title={message.errorMessage || "Erro ao enviar mensagem"}
          arrow
          interactive
          classes={{ tooltip: classes.errorTooltip, arrow: classes.errorTooltipArrow }}
        >
          <AlertCircle size={18} className={classes.ackErrorIcon} />
        </Tooltip>
      );
    }
    if (message.ack === 0 || message.ack === undefined || message.ack === null) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <CheckCheck size={18} className={classes.ackIcons} />;
    }
    if (message.ack >= 3) {
      return <CheckCheck size={18} className={classes.ackDoneAllIcon} />;
    }
  };

  const renderReactions = (message) => {
    if (!message.reactions || !Array.isArray(message.reactions) || message.reactions.length === 0) {
      return null;
    }
    const groupedReactions = groupReactions(message.reactions);
    return (
      <div className={classes.reactionsContainer}>
        {groupedReactions.map((reaction, index) => (
          <div key={`${message.id}-reaction-${index}`} className={classes.reactionBubble}>
            <span className={classes.reactionEmoji}>{reaction.emoji}</span>
            {reaction.count > 1 && (
              <span className={classes.reactionCount}>{reaction.count}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
        style={{ cursor: message.quotedMsg?.id ? 'pointer' : 'default' }}
        onClick={() => message.quotedMsg?.id && onQuotedClick(message.quotedMsg.id)}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {isAudio(message.quotedMsg.mediaType) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 4, paddingBottom: 2 }}>
              <AudiotrackIcon style={{ fontSize: 18, opacity: 0.9 }} />
              <span>{i18n.t("messagesList.audio") || "Áudio"}</span>
            </div>
          )}
          {message.quotedMsg.mediaType === "locationMessage" && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 4, paddingBottom: 2 }}>
              <MapPin size={18} style={{ opacity: 0.9, color: '#d32f2f' }} />
              <span>{i18n.t("messagesList.location") || "Localização"}</span>
            </div>
          )}
          {isVideo(message.quotedMsg.mediaType) && (
            <VideoPlayer videoUrl={message.quotedMsg.mediaUrl} className={classes.messageMedia} />
          )}
          {isDocument(message.quotedMsg.mediaType) && (() => {
            const { fileName, fileSize, ext } = getDocumentInfo(message.quotedMsg);
            const extColor = DOC_EXT_COLORS[ext] || "#667781";
            return (
              <div className={classes.quotedDocCard}>
                <div className={classes.quotedDocIcon} style={{ backgroundColor: extColor }}>
                  {ext || "DOC"}
                </div>
                <div className={classes.quotedDocInfo}>
                  <div className={classes.quotedDocName} title={fileName}>
                    {fileName}
                  </div>
                  <div className={classes.quotedDocMeta}>
                    {[ext, fileSize].filter(Boolean).join(" • ")}
                  </div>
                </div>
              </div>
            );
          })()}
          {isImage(message.quotedMsg.mediaType) && (
            <div style={{ borderRadius: 6, overflow: 'hidden' }}>
              <RotatableImage imageUrl={message.quotedMsg.mediaUrl} />
            </div>
          )}
          {message.quotedMsg.mediaType === "contactMessage" && (
            <span>{sharedContactsLabel(message.quotedMsg.body) || message.quotedMsg.body}</span>
          )}
          {message.quotedMsg?.body &&
           !isImage(message.quotedMsg.mediaType) &&
           !isAudio(message.quotedMsg.mediaType) &&
           !isVideo(message.quotedMsg.mediaType) &&
           !isDocument(message.quotedMsg.mediaType) &&
           message.quotedMsg.mediaType !== "contactMessage" &&
           message.quotedMsg.mediaType !== "locationMessage" && (
            <span>{getExcerpt(message.quotedMsg.body)}</span>
          )}
        </div>
      </div>
    );
  };

  if (!message.fromMe) {
    return (
      <div className={clsx(classes.messageLeft, {
        [classes.messageSelected]: forwardMode && selectedMessages.includes(message.id),
        [classes.messageLeftMedia]: message.mediaType === "image" || isImageFile(message.mediaUrl, message.body),
        [classes.stickerBubble]: message.mediaType === "sticker"
      })} style={{ marginLeft: forwardMode ? 40 : 0 }}>
        {forwardMode && (
          <Checkbox
            checked={selectedMessages.includes(message.id)}
            onChange={() => onMessageSelect(message.id)}
            color="primary"
            size="small"
            style={{ position: "absolute", left: -40, top: 8, padding: 4, zIndex: 10 }}
          />
        )}
        <IconButton
          variant="contained"
          size="small"
          id="messageActionsButton"
          disabled={message.isDeleted}
          className={classes.messageActionsButton}
          onClick={(e) => onOpenMessageOptionsMenu(e, message)}
          style={{ display: forwardMode ? "none" : undefined }}
        >
          <ChevronDown />
        </IconButton>
        {isGroup && !message.fromMe && (
          <span className={classes.messageContactName} style={{ color: getStableColor(getSenderName(message, isGroup)) }}>
            {getSenderName(message, isGroup)}
          </span>
        )}
        {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "vcard") && checkMessageMedia(message)}
        <div id={`message-${message.id}`} className={clsx(classes.textContentItem, {
          [classes.textContentItemDeleted]: message.isDeleted,
          [classes.textContentItemCard]: hasContactCard,
        })}>
          {message.isDeleted && (
            <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
              <Ban color="disabled" size={18} className={classes.deletedIcon} />
              <span style={{
                fontSize: 12,
                fontStyle: "italic",
                color: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.36)" : "rgba(0, 0, 0, 0.5)"
              }}>
                {i18n.t("messagesList.deletedMessage")}
              </span>
            </div>
          )}
          {renderForwardedIndicator(message, theme)}
          {message.quotedMsg && renderQuotedMessage(message)}
          {/* ✅ Não renderizar body se já foi renderizado dentro da mídia (imagem/vídeo/áudio) */}
          {renderMessageContent()}
          <span className={classes.timestamp}>
            {message.isEdited && <span>Editada </span>}
            {format(parseISO(message.createdAt), "HH:mm")}
          </span>
        </div>
        {renderReactions(message)}
      </div>
    );
  } else {
    // Enviada pela IA (triagem, agente do fluxo ou usuário IA): o nome dela fica gravado na mensagem.
    const aiSender = message.mediaType !== "sticker" ? message.aiSenderName : null;
    return (
      <div className={clsx(classes.messageRight, {
        [classes.messageAi]: !!aiSender,
        [classes.messageSelected]: forwardMode && selectedMessages.includes(message.id),
        [classes.messageRightMedia]: message.mediaType === "image" || isImageFile(message.mediaUrl, message.body),
        [classes.stickerBubble]: message.mediaType === "sticker"
      })} style={{ marginLeft: forwardMode ? 40 : 0 }}>
        {forwardMode && (
          <Checkbox
            checked={selectedMessages.includes(message.id)}
            onChange={() => onMessageSelect(message.id)}
            color="primary"
            size="small"
            style={{ position: "absolute", left: -40, top: 8, padding: 4, zIndex: 10 }}
          />
        )}
        <IconButton
          variant="contained"
          size="small"
          id="messageActionsButton"
          disabled={message.isDeleted}
          className={classes.messageActionsButton}
          onClick={(e) => onOpenMessageOptionsMenu(e, message)}
          style={{ display: forwardMode ? "none" : undefined }}
        >
          <ChevronDown />
        </IconButton>
        {aiSender && (
          <div className={classes.aiHeader} title={i18n.t("messagesList.aiSentBy")}>
            <span className={classes.aiAvatar} aria-hidden="true">
              <Bot />
            </span>
            <span className={classes.aiName}>{aiSender}</span>
            <span className={classes.aiBadge}>{i18n.t("messagesList.aiBadge")}</span>
          </div>
        )}
        {smartApproachMark && (
          <div className={classes.autoHeader} title={smartApproachText("messageMarkTitle")}>
            <Sparkles aria-hidden="true" />
            <span>
              {smartApproachText("messageMark", {
                step: ordinal(smartApproachMark.step, smartApproachText),
                total: smartApproachMark.total,
              })}
            </span>
          </div>
        )}
        {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "vcard") && checkMessageMedia(message)}
        <div id={`message-${message.id}`}
          className={clsx(classes.textContentItem, {
            [classes.textContentItemDeleted]: message.isDeleted,
            [classes.textContentItemEdited]: message.isEdited && !message.isDeleted,
            [classes.textContentItemCard]: hasContactCard,
          })}
        >
          {message.isDeleted && (
            <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
              <Ban color="disabled" size={18} className={classes.deletedIcon} />
              <span style={{
                fontSize: 12,
                fontStyle: "italic",
                color: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.36)" : "rgba(0, 0, 0, 0.5)"
              }}>
                {i18n.t("messagesList.deletedMessage")}
              </span>
            </div>
          )}
          {renderForwardedIndicator(message, theme)}
          {message.quotedMsg && renderQuotedMessage(message)}
          {/* ✅ Não renderizar body se já foi renderizado dentro da mídia (imagem/vídeo/áudio) */}
          {renderMessageContent()}
          <span className={classes.timestamp}>
            {message.isEdited && <span>{i18n.t("messagesList.edited")} </span>}
            {format(parseISO(message.createdAt), "HH:mm")}
            {renderMessageAck(message)}
          </span>
        </div>
        {renderReactions(message)}
      </div>
    );
  }
}, (prevProps, nextProps) => {
  // ✅ Comparação customizada para evitar re-renders desnecessários

  // Comparação básica de propriedades
  if (
    prevProps.message.id !== nextProps.message.id ||
    prevProps.message.ack !== nextProps.message.ack ||
    prevProps.message.isDeleted !== nextProps.message.isDeleted ||
    prevProps.message.isEdited !== nextProps.message.isEdited ||
    prevProps.message.dataJson !== nextProps.message.dataJson ||
    prevProps.message.aiSenderName !== nextProps.message.aiSenderName ||
    prevProps.forwardMode !== nextProps.forwardMode ||
    prevProps.selectedMessages.includes(prevProps.message.id) !== nextProps.selectedMessages.includes(nextProps.message.id)
  ) {
    return false; // Props mudaram, re-renderizar
  }

  // ✅ Comparação otimizada de reactions (sem JSON.stringify)
  const prevReactions = prevProps.message.reactions;
  const nextReactions = nextProps.message.reactions;

  // Comparação rápida por referência primeiro
  if (prevReactions === nextReactions) {
    return true; // Mesma referência, não re-renderizar
  }

  // Se um é null/undefined e o outro não, mudou
  if (!prevReactions || !nextReactions) {
    return prevReactions === nextReactions;
  }

  // Se tamanhos diferentes, mudou
  if (prevReactions.length !== nextReactions.length) {
    return false;
  }

  // Comparação elemento por elemento (mais rápido que JSON.stringify)
  for (let i = 0; i < prevReactions.length; i++) {
    const prev = prevReactions[i];
    const next = nextReactions[i];

    if (
      prev.text !== next.text ||
      prev.fromMe !== next.fromMe ||
      prev.userId !== next.userId
    ) {
      return false; // Reação mudou, re-renderizar
    }
  }

  return true; // Nenhuma mudança detectada, não re-renderizar
});

export default MessageItem;
